import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSettings } from '@wu/widget-core';
import type { Interval } from 'date-fns';
import { parseISO } from 'date-fns';
import { isCalendarIntegrationEnabled } from '../../common/is-calendar-integration-enabled';
import { APPOINTMENT_BOOKING_API_BASE_URL } from '../../constants/constants';
import { useEventContext } from '../event-provider';
import { getSessionId } from '../utils/get-session-id';

export type CalendarLockedTimeslotResponseItem = {
  from: string;
  to: string;
  timeZone: string;
};

export function useLockedTimeslotsQuery({ start, end }: Interval) {
  const {
    widgetId,
    widgetToken,
    integrationGoogleCalendarCalendar,
    integrationGoogleCalendarAuth
  } = useSettings();
  const queryClient = useQueryClient();
  const { subscribe } = useEventContext();
  const isGCEnabled = isCalendarIntegrationEnabled({
    integrationGoogleCalendarCalendar,
    integrationGoogleCalendarAuth
  });

  useEffect(() => {
    const unsubscribe = subscribe('resetAll', () => {
      queryClient.resetQueries({ queryKey: ['calendar-locked-timeslots'] });
    });

    return () => {
      unsubscribe();
    };
  }, []);

  return useQuery<Interval[] | undefined>({
    queryKey: [
      'calendar-locked-timeslots',
      isGCEnabled,
      widgetToken,
      widgetId,
      start,
      end
    ],
    queryFn: async () => {
      if (!widgetToken || !isGCEnabled) {
        return;
      }

      const widgetIdEncoded = encodeURIComponent(widgetId || '');

      const params = new URLSearchParams({
        appointmentId: widgetIdEncoded
      });

      const response = await fetch(
        `${APPOINTMENT_BOOKING_API_BASE_URL}/appointment-booking/timeslots/locked?${params}`,
        {
          method: 'GET',
          headers: new Headers({
            'x-session-id': getSessionId(),
            'x-widget-token': widgetToken || ''
          })
        }
      );

      const text = await response.text();

      if (!text) return [];

      const data = JSON.parse(text) as unknown as {
        payload: CalendarLockedTimeslotResponseItem[];
        code: number;
        message: string;
      };

      const removeTimeZoneRegexp = /([+-]\d{2}:\d{2}|Z)$/;

      return data.payload.map(({ from, to }) => {
        return {
          start: parseISO(from.replace(removeTimeZoneRegexp, '')),
          end: parseISO(to.replace(removeTimeZoneRegexp, ''))
        };
      });
    },
    enabled: !!widgetToken && !!isGCEnabled,
    refetchInterval: 1000 * 60,
    refetchOnWindowFocus: true
  });
}
